import { AutoSuggestInput } from '@popsure/dirty-swan';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { useEffect, useState } from 'react';
import { fuzzySearchOptions } from '../../../utils/fuzzySearchOptions';

import { Header } from '../../Screen';

type Option<ValueType> = {
  value: ValueType;
  label: string;
};

type Props<Questionnaire extends QuestionnaireQuestions, ValueType> = {
  options: Option<ValueType>[];
} & QuestionnaireFormProps<Questionnaire, Option<ValueType>>;

export const DropdownSingleForm = <
  Questionnaire extends QuestionnaireQuestions,
  ValueType extends string
>({
  options,
  value,
  question,
  onSubmitValue,
  featureName,
  setValidForSubmission,
  placeholder = '',
}: Props<Questionnaire, ValueType>) => {
  const [answer, setAnswer] = useState<Option<ValueType> | undefined>(value);
  const [currentOption, setCurrentOption] = useState<ValueType | string>(
    answer?.label ?? ''
  );
  const [suggestions, setSuggestions] = useState<Option<ValueType>[]>([]);

  const handleSubmit = () => {
    if (!answer) {
      throw new Error(
        `[${featureName}] Dropdown value is missing for submission on question: ${question}`
      );
    }
    onSubmitValue(answer);
  };

  useEffect(() => {
    setValidForSubmission(Boolean(
      answer?.value && options.find((option) => option.value === answer.value)
    ));
  }, [setValidForSubmission, answer, options]);

  return (
    <Header onSubmit={handleSubmit}>
      <AutoSuggestInput
        currentOption={currentOption}
        placeholder={placeholder}
        suggestions={suggestions}
        onChange={(val) => {
          setCurrentOption(val);
          const selectedOption = options.find((option) => option.label === val);
          setAnswer(selectedOption);
        }}
        handleSuggestionSelected={(val) => {
          setCurrentOption(val.value);
          const selectedOption = options.find(
            (option) => option.value === val.value
          );
          setAnswer(selectedOption);
        }}
        handleSuggestionFetchRequest={({ value: val }) => {
          const filteredOptions = fuzzySearchOptions(val, options, 'label');
          setSuggestions(filteredOptions);
        }}
        handleSuggestionClearRequest={() => setSuggestions([])}
      />
    </Header>
  );
};
